var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useState, useEffect, useRef } from 'react';
import { Field } from 'react-final-form';
import { StyledInputLabel, InputElementHeading } from '../elements';
import { Input } from '../input/input';
import { validKeys, format, formatWithDecimals, containsDecimals, fixToDecimals, getNumDecimalPlaces, appendMissingZeros, adaptValueToFormat, getFinalValueWithFormat, getNumberOfCommas, calculateCursorPosition, } from '../helpers';
import AccessibilityHelper from '../accessibilityHelper';
var currencyFieldI18nDefaults = {
    ariaLabelExtension: ', in pounds',
};
var InputCurrency = React.memo(function (_a) {
    var id = _a.id, label = _a.label, hint = _a.hint, input = _a.input, testId = _a.testId, name = _a.name, meta = _a.meta, required = _a.required, placeholder = _a.placeholder, readOnly = _a.readOnly, width = _a.inputWidth, cfg = _a.cfg, after = _a.after, before = _a.before, callback = _a.callback, _b = _a.decimalPlaces, decimalPlaces = _b === void 0 ? 2 : _b, noLeftBorder = _a.noLeftBorder, optionalText = _a.optionalText, _c = _a.maxInputLength, maxInputLength = _c === void 0 ? 16 + decimalPlaces : _c, initialValue = _a.initialValue, _d = _a.i18n, i18n = _d === void 0 ? currencyFieldI18nDefaults : _d, props = __rest(_a, ["id", "label", "hint", "input", "testId", "name", "meta", "required", "placeholder", "readOnly", "inputWidth", "cfg", "after", "before", "callback", "decimalPlaces", "noLeftBorder", "optionalText", "maxInputLength", "initialValue", "i18n"]);
    var currencySymbol = '£';
    var digits = [
        '0',
        '1',
        '2',
        '3',
        '4',
        '5',
        '6',
        '7',
        '8',
        '9',
        '.',
        currencySymbol,
    ];
    var _e = useState(''), inputValue = _e[0], setInputValue = _e[1];
    var _f = useState(''), formattedInputValue = _f[0], setFormattedInputValue = _f[1];
    var _g = useState(false), dot = _g[0], setDot = _g[1];
    var _h = useState(null), cursorPos = _h[0], setCursorPos = _h[1];
    var _j = useState(false), delKey = _j[0], setDelKey = _j[1];
    var formatWithCommas = function (value) {
        var hasCurrencySymbol = value && value[0] === currencySymbol;
        var numString = value
            .replace(/,/g, '')
            .replace(currencySymbol, '');
        if (numString === '')
            return value;
        var numFormatted = '';
        if (!containsDecimals(value)) {
            numFormatted =
                (hasCurrencySymbol ? currencySymbol : '') + format(numString);
            var numWithDecimals = fixToDecimals(numString, decimalPlaces);
            setFormattedInputValue(numFormatted + '.' + numWithDecimals.slice(-decimalPlaces));
        }
        else {
            numFormatted =
                (hasCurrencySymbol ? currencySymbol : '') +
                    formatWithDecimals(numString, decimalPlaces);
            setFormattedInputValue(numFormatted);
        }
        return numFormatted;
    };
    var keyPressedIsNotAllowed = function (e) {
        if (!digits.includes(e.key) && !validKeys.includes(e.key))
            return true;
        return false;
    };
    var handleKeyDown = function (e) {
        if (!(e.ctrlKey === true)) {
            if (e.key === '.') {
                dot ? e.preventDefault() : setDot(true);
                return true;
            }
            if (e.key === currencySymbol) {
                if (e.target.value.length !== 0)
                    e.preventDefault();
                return true;
            }
            keyPressedIsNotAllowed(e) && e.preventDefault();
            setCursorPos(e.target.selectionStart);
            e.key === 'Delete' ? setDelKey(true) : setDelKey(false);
        }
    };
    var valueLengthValid = function (value) {
        if (value) {
            var newValue = getFinalValueWithFormat(value, decimalPlaces);
            if (newValue.length > maxInputLength)
                return false;
        }
        return true;
    };
    var handleOnChange = function (e) {
        var _a;
        var commasBefore = getNumberOfCommas(inputValue, cursorPos);
        if (!valueLengthValid(e.target.value)) {
            e.target.value = formattedInputValue;
        }
        else {
            setInputValue(e.target.value);
            if (String(e.target.value)[e.target.value.length - 1] == '.') {
                input.onChange(e.target.value);
            }
            else {
                if (e.target.value) {
                    e.target.value = formatWithCommas(e.target.value);
                    if (cursorPos !== e.target.value.length) {
                        _a = calculateCursorPosition(cursorPos, e, inputValue, commasBefore, delKey), e.target.selectionStart = _a[0], e.target.selectionEnd = _a[1];
                    }
                    setInputValue(e.target.value);
                    input.onChange(e.target.value && e.target.value);
                }
                else
                    input.onChange(null);
            }
            if (!containsDecimals(e.target.value))
                setDot(false);
            e.target.value === '' && setFormattedInputValue('');
        }
        if (callback) {
            var numericValue = Number(adaptValueToFormat(e.target.value.replace(/,/g, ''), decimalPlaces));
            e.target.value === ''
                ? callback(null)
                : callback(Number(numericValue.toFixed(decimalPlaces)));
        }
    };
    var handleBlur = function (e) {
        input.onBlur(e);
        e.target.value =
            getNumDecimalPlaces(formattedInputValue) < decimalPlaces
                ? appendMissingZeros(inputValue.replace(/,/g, '').replace(currencySymbol, ''), decimalPlaces)
                : formattedInputValue.replace(currencySymbol, '');
        setInputValue(e.target.value);
        input.onChange(e);
    };
    var formatInitialValue = function (value) {
        var newInitialValue = formatWithCommas(value.toFixed(decimalPlaces));
        setFormattedInputValue(newInitialValue);
        setInputValue(newInitialValue);
        innerInput.current.value = newInitialValue;
    };
    var innerInput = useRef(null);
    useEffect(function () {
        if (initialValue !== undefined && initialValue !== null) {
            var myEvent_1 = new Event('blur', { bubbles: true });
            formatInitialValue(initialValue);
            setTimeout(function () {
                innerInput.current.dispatchEvent(myEvent_1);
            }, 100);
        }
        else {
            setFormattedInputValue('');
            setInputValue('');
            innerInput.current.value = null;
            input.onChange(null);
        }
    }, [initialValue]);
    var helper = new AccessibilityHelper(name, !!label, !!hint);
    return (React.createElement(StyledInputLabel, { isError: meta && meta.touched && meta.error, cfg: Object.assign({ flexDirection: 'column', mt: 1 }, cfg), noLeftBorder: noLeftBorder },
        React.createElement(InputElementHeading, { label: label, required: optionalText !== undefined ? !optionalText : required, hint: hint, meta: meta, accessibilityHelper: helper }),
        React.createElement(Input, __assign({ id: id, name: name, parentRef: innerInput, type: "text", width: width, testId: testId, label: label, ariaLabelExtension: i18n.ariaLabelExtension, isError: meta && meta.touched && meta.error, placeholder: placeholder, readOnly: readOnly, decimalPlaces: decimalPlaces }, input, { onKeyDown: handleKeyDown, onChange: handleOnChange, onBlur: handleBlur, after: after, before: before, accessibilityHelper: helper }, props))));
});
export var FFInputCurrency = function (fieldProps) {
    return (React.createElement(Field, __assign({}, fieldProps, { render: function (props) { return (React.createElement(InputCurrency, __assign({}, props, { name: fieldProps.name, initialValue: fieldProps.initialValue }))); } })));
};
//# sourceMappingURL=currency.js.map